import { useEffect, useState, type ReactNode } from 'react';
import { CalendarPlus, Search, Sparkles, X } from 'lucide-react';
import { cn } from './ui/utils';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { useWorkspaceSearchOpener } from './workspace-search';
import { useAgentChatOpener } from './agent-chat';
import { useWorkspace } from '@/contexts/context';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@saas-boilerplate/shared';

export type AppTopBarProps = {
  // Identity, Help and Sign out now live in the sidebar account block, so these are accepted for
  // call-site compatibility but no longer rendered here. Kept for future use (e.g. breadcrumbs).
  userProfile?: { full_name?: string; email?: string; role?: string } | null;
  workspaceName?: string | null;
  onHelp?: () => void;
  onSignOut?: () => void;
  onSwitchWorkspace?: () => void;
  /** Set when the bar is already inside a full-bleed container (e.g. negated main padding). */
  noBleed?: boolean;
  /** Optional breadcrumb / page-nav node rendered in the left slot (e.g. Meetings › {title}). */
  breadcrumb?: ReactNode;
  /** Navigate to Integrations for the connect-calendar banner. Falls back to a hard /integrations
   *  navigation when the render site has no navigation callback to hand. */
  onConnectCalendar?: () => void;
};

type CalConnState = 'unknown' | 'connected' | 'none' | 'needs_reconnect';

/** Short-TTL module cache so view switches don't refetch on every AppTopBar mount. */
const connStateCache = new Map<string, { state: CalConnState; at: number }>();
const CONN_CACHE_TTL_MS = 60_000;

function useCalendarConnectionState(workspaceId?: string | null, userId?: string | null): CalConnState {
  const [state, setState] = useState<CalConnState>('unknown');
  useEffect(() => {
    if (!workspaceId || !userId) return;
    const key = `${workspaceId}:${userId}`;
    const cached = connStateCache.get(key);
    if (cached && Date.now() - cached.at < CONN_CACHE_TTL_MS) {
      setState(cached.state);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('calendar_connections')
        .select('provider, needs_reconnect')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId);
      if (cancelled) return;
      let next: CalConnState = 'unknown';
      if (!error) {
        const rows = (data || []) as Array<{ needs_reconnect?: boolean | null }>;
        if (rows.some((r) => !r.needs_reconnect)) next = 'connected';
        else if (rows.length > 0) next = 'needs_reconnect';
        else next = 'none';
      }
      connStateCache.set(key, { state: next, at: Date.now() });
      setState(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, userId]);
  return state;
}

const BANNER_DISMISS_MS = 7 * 24 * 3600 * 1000;
const bannerDismissKey = (workspaceId: string) => `upstand.connect-calendar-banner.${workspaceId}`;
const autoJoinDismissKey = (workspaceId: string) => `upstand.auto-join-banner.${workspaceId}`;

/** Module cache mirroring connStateCache: has this user enabled auto-enrol in this workspace? */
const autoJoinCache = new Map<string, { enabled: boolean; at: number }>();

function useAutoJoinEnabled(workspaceId?: string | null, userId?: string | null): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    if (!workspaceId || !userId) return;
    const key = `${workspaceId}:${userId}`;
    const cached = autoJoinCache.get(key);
    if (cached && Date.now() - cached.at < CONN_CACHE_TTL_MS) {
      setEnabled(cached.enabled);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('auto_enrol_settings')
        .select('enabled')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      const value = error ? null : Boolean((data as { enabled?: boolean } | null)?.enabled);
      if (value !== null) autoJoinCache.set(key, { enabled: value, at: Date.now() });
      setEnabled(value);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, userId]);
  return enabled;
}

/**
 * Sticky top app bar - workspace Search + the Ask AI button, an optional breadcrumb in the left
 * slot, and (when the signed-in user has no working calendar connection in this workspace) a
 * dismissible connect-calendar banner. Workspace identity, Help and Sign out live in the sidebar.
 */
export function AppTopBar({ noBleed, breadcrumb, onConnectCalendar }: AppTopBarProps) {
  const openWorkspaceSearch = useWorkspaceSearchOpener();
  const openAgentChat = useAgentChatOpener();
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const connState = useCalendarConnectionState(workspace?.id, user?.id);
  const autoJoinEnabled = useAutoJoinEnabled(workspace?.id, user?.id);
  const [dismissed, setDismissed] = useState(true); // hidden until the dismissal check runs
  const [autoJoinDismissed, setAutoJoinDismissed] = useState(true);

  useEffect(() => {
    if (!workspace?.id || connState !== 'connected' || autoJoinEnabled !== false) return;
    try {
      const raw = localStorage.getItem(autoJoinDismissKey(workspace.id));
      const at = raw ? Number(JSON.parse(raw)?.at || 0) : 0;
      setAutoJoinDismissed(Boolean(at) && Date.now() - at < BANNER_DISMISS_MS);
    } catch {
      setAutoJoinDismissed(false);
    }
  }, [workspace?.id, connState, autoJoinEnabled]);

  const dismissAutoJoinBanner = () => {
    setAutoJoinDismissed(true);
    if (!workspace?.id) return;
    try {
      localStorage.setItem(autoJoinDismissKey(workspace.id), JSON.stringify({ at: Date.now() }));
    } catch {
      /* session-only dismissal */
    }
  };

  useEffect(() => {
    if (!workspace?.id) return;
    if (connState !== 'none' && connState !== 'needs_reconnect') return;
    try {
      const raw = localStorage.getItem(bannerDismissKey(workspace.id));
      if (!raw) {
        setDismissed(false);
        return;
      }
      const parsed = JSON.parse(raw) as { state?: string; at?: number };
      // A dismissal only covers the state it was made in - a later needs_reconnect still shows.
      setDismissed(parsed.state === connState && Date.now() - (parsed.at || 0) < BANNER_DISMISS_MS);
    } catch {
      setDismissed(false);
    }
  }, [workspace?.id, connState]);

  const dismissBanner = () => {
    setDismissed(true);
    if (!workspace?.id) return;
    try {
      localStorage.setItem(bannerDismissKey(workspace.id), JSON.stringify({ state: connState, at: Date.now() }));
    } catch {
      /* localStorage unavailable - session-only dismissal */
    }
  };

  const handleConnect = () => {
    if (onConnectCalendar) onConnectCalendar();
    else window.location.assign('/integrations');
  };

  const showBanner = !dismissed && (connState === 'none' || connState === 'needs_reconnect');
  // Opt-in nudge for existing users: calendar connected but auto-join never enabled. The connect
  // banner always wins - one banner at a time.
  const showAutoJoinBanner =
    !showBanner && !autoJoinDismissed && connState === 'connected' && autoJoinEnabled === false;

  return (
    <header
      className={cn(
        'sticky top-0 z-20 mb-4 flex flex-col gap-2 border-b border-border/40 bg-background/80 px-2 py-2 backdrop-blur-xl',
        noBleed ? '' : '-mx-4 sm:-mx-0',
      )}
    >
      {showBanner && (
        <div className="flex items-center gap-2.5 rounded-md border border-amber-300/60 bg-amber-50/90 px-3 py-1.5 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
          <CalendarPlus className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
          <span className="min-w-0 flex-1 truncate">
            {connState === 'needs_reconnect'
              ? 'Your calendar connection needs attention - reconnect it to keep meetings in sync.'
              : 'Connect your calendar to see your meetings here and enrol them in a click.'}
          </span>
          <Button
            type="button"
            size="sm"
            className="h-6 shrink-0 rounded-full px-3 text-[11px] font-medium"
            onClick={handleConnect}
          >
            {connState === 'needs_reconnect' ? 'Reconnect' : 'Connect calendar'}
          </Button>
          <button
            type="button"
            onClick={dismissBanner}
            aria-label="Dismiss"
            className="shrink-0 rounded p-0.5 text-amber-900/60 transition-colors hover:text-amber-900 dark:text-amber-100/60 dark:hover:text-amber-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {showAutoJoinBanner && (
        <div className="flex items-center gap-2.5 rounded-md border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs text-foreground/90">
          <CalendarPlus className="h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={1.8} />
          <span className="min-w-0 flex-1 truncate">
            Auto-join is off - Upstand can automatically enrol your upcoming meetings,
            no per-meeting action.
          </span>
          <Button
            type="button"
            size="sm"
            className="h-6 shrink-0 rounded-full px-3 text-[11px] font-medium"
            onClick={() => window.location.assign('/settings')}
          >
            Turn on auto-join
          </Button>
          <button
            type="button"
            onClick={dismissAutoJoinBanner}
            aria-label="Dismiss"
            className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        {/* Left slot: page breadcrumbs (e.g. Meetings › {title}). Workspace identity lives in the sidebar. */}
        {breadcrumb ? <div className="flex min-w-0 items-center">{breadcrumb}</div> : null}
        <div className="flex flex-1 flex-wrap items-center justify-end gap-2 sm:gap-3">
          {/* Two distinct entry points: a Search field (find existing content) and the Ask AI button.
              Styled differently + divided so they don't read as one control. Shortcuts live in the
              hover tooltips rather than inline badges (DR-3). */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => openWorkspaceSearch()}
                className={cn(
                  'relative hidden min-w-0 flex-1 items-center gap-2 rounded-full border border-stone-200/70 bg-stone-100/80 px-3 py-1.5 text-left text-sm text-muted-foreground shadow-none ring-0 transition-colors hover:bg-stone-100 md:flex md:max-w-xs',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
                )}
                aria-label="Search workspace"
              >
                <Search className="h-4 w-4 shrink-0 opacity-70" />
                <span className="min-w-0 flex-1 truncate">Search your workspace…</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Search workspace · ⌘K</TooltipContent>
          </Tooltip>
          <div className="mx-0.5 hidden h-5 w-px shrink-0 bg-border/60 md:block" aria-hidden />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 text-xs font-medium text-primary hover:bg-primary/10"
                onClick={openAgentChat}
                aria-label="Ask Upstand"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Ask
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Ask Upstand · ⌘J</TooltipContent>
          </Tooltip>
          {/* Notifications bell hidden until the feature ships. */}
        </div>
      </div>
    </header>
  );
}
